/**
 * OBS scene runtime state - the PWA-side mirror of `GET /obs/scenes` on each
 * bridge that an OBS control points at.
 *
 * Mirrors `toggle/runtime.ts` in shape (plain signal over an immutable
 * record, never a Map - a Solid read of a Map tracks nothing, and the
 * resulting dep sits forever on a replaced object). Keyed by BRIDGE ID, not
 * globally, because OBS is a per-machine program: the streaming PC's bridge
 * and the music PC's bridge answer different scene lists, and one global
 * "current scene" would show the wrong machine's state with no way to tell.
 *
 * # Why the status is a named union and not a boolean
 *
 * The four ways this can fail all render differently and all have different
 * fixes, so collapsing them into "no scenes" would produce the exact dead
 * control DEC-138 exists to prevent:
 *
 * - `unauthorized` - OPBridge's `opbuttons-full` bundle does not grant the
 *   `/obs/scenes` + `/obs/scene/**` scopes. MEASURED 2026-09-19 against the
 *   running daemon: v14 grants neither, so this is the state every rig is in
 *   today until that bundle is bumped. The fix is on OPBridge's side and the
 *   operator can do nothing about it, so it must say so rather than look
 *   like a broken OBS.
 * - `unavailable` - a 503: the bridge is up but OBS is not running, or is
 *   running without obs-websocket enabled. The fix is "start OBS".
 * - `error` - anything else (a 404 on a daemon too old to have the route, a
 *   422, a parse failure).
 * - `unknown` - never asked. NOT the same as "no scenes exist"; a control
 *   must not claim a scene is missing before the first read lands.
 */

import { createSignal, type Accessor } from 'solid-js';

/** How the last read of one bridge's scene list went. */
export type ObsStatus = 'unknown' | 'ok' | 'unauthorized' | 'unavailable' | 'error';

export interface ObsSceneState {
  status: ObsStatus;
  /** The live PROGRAM scene. `null` is a real OBS state (program-scene
   *  desync), not an error, and is also what a non-`ok` status carries. */
  current: string | null;
  /** Every scene OBS knows, in OBS's own order. Empty on any non-`ok`
   *  status - never a stale list from a previous read, because a stale list
   *  would let the Inspector offer a scene that may no longer exist. */
  scenes: string[];
  /** `Date.now()` of the last completed read attempt, 0 if never. */
  at: number;
  /** Human-readable cause, for the dead-state copy. */
  detail?: string;
}

const EMPTY: ObsSceneState = { status: 'unknown', current: null, scenes: [], at: 0 };

const [statesSignal, setStatesSignal] = createSignal<Record<string, ObsSceneState>>({});

/** The whole per-bridge mirror. Reactive. */
export const obsStates: Accessor<Record<string, ObsSceneState>> = statesSignal;

/** One bridge's last-known OBS state. Never `undefined` - an unread bridge
 *  reads as `status: 'unknown'`, so callers never have to distinguish
 *  "absent key" from "not asked yet". */
export function obsState(bridgeId: string): ObsSceneState {
  return statesSignal()[bridgeId] ?? EMPTY;
}

/** Record a successful read. */
export function recordObsScenes(
  bridgeId: string,
  current: string | null,
  scenes: string[],
): void {
  setStatesSignal((prev) => ({
    ...prev,
    [bridgeId]: { status: 'ok', current, scenes, at: Date.now() },
  }));
}

/** Record a failed read, preserving WHICH failure. */
export function recordObsFailure(
  bridgeId: string,
  status: Exclude<ObsStatus, 'ok' | 'unknown'>,
  detail?: string,
): void {
  setStatesSignal((prev) => ({
    ...prev,
    [bridgeId]: {
      status,
      current: null,
      scenes: [],
      at: Date.now(),
      ...(detail ? { detail } : {}),
    },
  }));
}

/**
 * Record a scene switch WE just made and the bridge acked.
 *
 * This is an optimistic write against a readback we would otherwise wait up
 * to one poll interval for, and it is safe for exactly the reason OPBridge's
 * `set_scene` is commit-on-ack: the ack IS obs-websocket's own
 * `RequestResponse` success, so the scene has changed in OBS by the time we
 * get here. It is not a guess about a send that might not have landed.
 *
 * It only ever moves `current`, never the scene LIST, and only on a bridge
 * whose list we have actually read - writing a list we inferred would be the
 * "intent masquerading as confirmed truth" this app has a locked rule
 * against (PLAN.md, Rig State feedback honesty).
 */
export function recordObsSceneSwitch(bridgeId: string, scene: string): void {
  setStatesSignal((prev) => {
    const cur = prev[bridgeId];
    if (!cur || cur.status !== 'ok') return prev;
    if (cur.current === scene) return prev;
    return { ...prev, [bridgeId]: { ...cur, current: scene } };
  });
}

/**
 * Why an OBS button cannot fire, or `null` if nothing is wrong.
 *
 * Pure, and deliberately NOT a method on the component: this is the one
 * decision in the whole feature with real ordering in it, and a decision
 * that lives only inside a JSX closure is a decision that never gets tested.
 * FireButton renders `obsFaultLabel(obsButtonFault(...))`; the Inspector
 * reads the same union for its longer copy.
 *
 * THE ORDER IS THE CONTENT. Each rung is a precondition for reading the
 * next one honestly:
 *
 *   no-bridge      the operator has not said WHICH COMPUTER runs OBS, and
 *                  until they do there is no scene list to pick from - the
 *                  scenes belong to a machine. So this outranks no-scene:
 *                  telling someone to pick a scene while the machine is
 *                  still unchosen is naming step 2 while step 1 is owed.
 *                  It is also why dispatch REFUSES an unaimed button rather
 *                  than using the default bridge, which would be confidently
 *                  describing the wrong computer.
 *                  **Reachable only when there is a genuine choice** - the
 *                  Inspector auto-picks the sole candidate, so the operator's
 *                  normal one-OBS LAN never sees this rung at all.
 *                  `[ORDER CORRECTED 2026-09-19: no-scene used to come first,
 *                  on the reasoning that an unconfigured button is
 *                  unconfigured whatever the bridge says. That was true while
 *                  the machine was chosen in the generic "Sends to" band; it
 *                  stopped being true when the Computer picker became the
 *                  first row of the OBS editor. Caught by
 *                  obs_computer_autopicks_single_machine, which found a
 *                  freshly-dropped button on a two-machine rig reading "pick
 *                  a scene" when the machine was the outstanding decision.]`
 *   no-scene       aimed at a machine, but no scene named.
 *   unauthorized   we were not allowed to read the list, so we cannot know
 *                  whether the scene exists. Saying "scene not in OBS" here
 *                  would send the operator to OBS to fix a problem that
 *                  lives in OPBridge's scope bundle.
 *   unavailable    OBS is not answering; same reasoning, different owner.
 *   error          any other read failure; same reasoning again.
 *   scene-missing  ONLY reachable on a status of `ok`, i.e. we really did
 *                  read the list and the scene really is not in it.
 *
 * `unknown` (never read) yields `null`, not a fault: not having asked is not
 * evidence of anything, and a button that goes loud before its first read
 * would cry wolf on every single load.
 */
export type ObsButtonFault =
  | 'no-scene'
  | 'no-bridge'
  | 'unauthorized'
  | 'unavailable'
  | 'error'
  | 'scene-missing';

export function obsButtonFault(
  scene: string | undefined,
  bridgeIds: string[] | undefined,
  st: ObsSceneState,
): ObsButtonFault | null {
  if (!bridgeIds || bridgeIds.length === 0) return 'no-bridge';
  if (!scene) return 'no-scene';
  if (st.status === 'unauthorized') return 'unauthorized';
  if (st.status === 'unavailable') return 'unavailable';
  if (st.status === 'error') return 'error';
  if (st.status === 'ok' && !st.scenes.includes(scene)) return 'scene-missing';
  return null;
}

/** Face text for a fault. Short enough for a button face on a dark stage -
 *  the Inspector carries the long version. */
export function obsFaultLabel(fault: ObsButtonFault): string {
  switch (fault) {
    case 'no-scene':      return 'pick a scene';
    case 'no-bridge':     return 'pick a computer';
    case 'unauthorized':  return 'no OBS access';
    case 'unavailable':   return 'OBS not running';
    case 'error':         return 'OBS error';
    case 'scene-missing': return 'scene not in OBS';
  }
}

/**
 * Is this button's scene the LIVE program scene?
 *
 * Only ever true on a status of `ok`. An unread, unauthorized or unreachable
 * bridge reads false - not because the scene is known to be off, but because
 * we do not know, and an unlit button is the honest rendering of "we do not
 * know" while a lit one would be a claim.
 */
export function obsSceneIsLive(scene: string | undefined, st: ObsSceneState): boolean {
  if (!scene) return false;
  if (st.status !== 'ok') return false;
  return st.current === scene;
}

/**
 * Which of `bridgeIds` currently has a REACHABLE OBS.
 *
 * "Reachable" means a `GET /obs/scenes` came back 200 on that bridge - the
 * only evidence that exists, and the reason the Computer picker can be
 * annotated at all. A bridge we have not asked (`unknown`) is NOT counted:
 * it may well have OBS, we simply do not know, and counting it would let the
 * auto-pick below choose a machine on a guess.
 *
 * Order follows `bridgeIds`, so the caller controls determinism.
 */
export function obsCapableBridges(bridgeIds: string[]): string[] {
  const st = statesSignal();
  return bridgeIds.filter((id) => st[id]?.status === 'ok');
}

/**
 * The computer an OBS control should aim at when the operator has not
 * chosen one, or `null` to leave it unset and ask.
 *
 * THE OPERATOR'S OWN FRAMING, 2026-09-19: *"generally there would only be
 * one OBS running on the LAN"*. So the common rig must need NO machine
 * configuration at all - dropping an OBS button and picking a scene should
 * be the whole job. A picker that demands a machine when there is only one
 * possible answer is the "i wanna do this" bar failing on the first step.
 *
 * Two tiers, and the order matters:
 *
 *  1. **Exactly one bridge has a reachable OBS** -> that one, regardless of
 *     how many bridges are paired. This is the real answer: it is not a
 *     guess, it is the only machine that can do the job.
 *  2. **Nothing is known yet** (no bridge has answered 200 - today that is
 *     EVERY rig, because the `/obs/*` scopes are not granted) and exactly
 *     one bridge is paired -> that one. On a single-bridge rig there is no
 *     other machine to mean, so refusing to pick would be pedantry rather
 *     than safety, and the button still says "OBS not running" if it is not
 *     there. On a multi-bridge rig this tier deliberately does NOT fire.
 *
 * **Two or more OBS-capable machines returns `null` and the operator picks** -
 * that is the case the dropdown exists for, and auto-picking one of them is
 * exactly the silent misroute DEC-205 refuses. Sending to the wrong machine
 * comes back as a 503 that reads like "OBS is shut", so a wrong guess here
 * is not a visible error; it is a feature that appears broken.
 */
export function autoPickObsBridge(bridgeIds: string[]): string | null {
  const capable = obsCapableBridges(bridgeIds);
  if (capable.length === 1) return capable[0]!;
  if (capable.length > 1) return null;
  if (bridgeIds.length === 1) return bridgeIds[0]!;
  return null;
}

/**
 * One-line annotation for a machine in the Computer picker. Says what we
 * KNOW about that machine's OBS, never what we hope.
 *
 * `connected` is the BRIDGE's own link state and it outranks everything
 * else, because a machine we cannot reach at all was never asked about OBS.
 * Reporting "not checked" there is true and useless - it reads as the app
 * not having got round to it, when the actual answer is "that computer is
 * off or off the network", which is the one the operator can act on. Seen in
 * the render with a powered-down second machine, not reasoned about.
 */
export function obsBridgeSummary(st: ObsSceneState, connected = true): string {
  if (!connected) return 'offline';
  switch (st.status) {
    case 'ok':
      return st.scenes.length === 1 ? '1 scene' : `${st.scenes.length} scenes`;
    case 'unauthorized':  return 'no OBS access';
    case 'unavailable':   return 'no OBS here';
    case 'error':         return 'unreachable';
    case 'unknown':       return 'not checked';
  }
}

/** Test/teardown hook. */
export function _resetObsStateForTest(): void {
  setStatesSignal({});
}
